/**
 * Alignment Guides and Distance-to-Boundary Indicators.
 *
 * Renders visual guides on the SVG canvas during drag operations:
 * - Blue dashed snap lines when edges/centers align with other sections
 * - Gray dashed distance lines from section edges to yard boundaries
 *   with distance labels in yard units
 */

import type { SnapGuide, BoundaryDistance } from "./shapeUtils";

interface AlignmentGuidesProps {
  guides: SnapGuide[];
  boundaries: BoundaryDistance[];
  yardWidth: number;
  yardHeight: number;
  scale: number;
  visible: boolean;
}

function AlignmentGuides({
  guides,
  boundaries,
  yardWidth,
  yardHeight,
  scale,
  visible,
}: AlignmentGuidesProps) {
  if (!visible) return null;

  const fontSize = Math.max(7, 9 / scale);
  const guideStrokeWidth = 1 / scale;
  const boundaryStrokeWidth = 0.8 / scale;

  return (
    <g style={{ pointerEvents: "none" }}>
      {/* Snap alignment guides */}
      {guides.map((guide, i) => (
        <line
          key={`guide-${i}`}
          x1={guide.axis === "x" ? guide.position : 0}
          y1={guide.axis === "y" ? guide.position : 0}
          x2={guide.axis === "x" ? guide.position : yardWidth}
          y2={guide.axis === "y" ? guide.position : yardHeight}
          stroke="#7c3aed"
          strokeWidth={guideStrokeWidth}
          strokeDasharray={`${4 / scale} ${4 / scale}`}
          strokeOpacity={0.6}
        />
      ))}

      {/* Distance-to-boundary indicators */}
      {boundaries.map(
        (bd) =>
          bd.distance > 0 && (
            <g key={bd.side}>
              {/* Dashed line from section edge to boundary */}
              <line
                x1={bd.fromX}
                y1={bd.fromY}
                x2={bd.toX}
                y2={bd.toY}
                stroke="#94a3b8"
                strokeWidth={boundaryStrokeWidth}
                strokeDasharray={`${3 / scale} ${3 / scale}`}
                strokeOpacity={0.7}
              />
              {/* Distance label */}
              <rect
                x={(bd.fromX + bd.toX) / 2 - 14 / scale}
                y={(bd.fromY + bd.toY) / 2 - 6 / scale}
                width={28 / scale}
                height={12 / scale}
                rx={3 / scale}
                fill="white"
                fillOpacity={0.9}
                stroke="#cbd5e1"
                strokeWidth={0.5 / scale}
              />
              <text
                x={(bd.fromX + bd.toX) / 2}
                y={(bd.fromY + bd.toY) / 2 + 3 / scale}
                fontSize={fontSize}
                fill="#64748b"
                textAnchor="middle"
                fontWeight="500"
              >
                {bd.distance}
              </text>
            </g>
          ),
      )}
    </g>
  );
}

export default AlignmentGuides;
